import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const HeaderContainer = styled.header`
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 50;
  padding: 1rem 0;
`;

const HeaderContent = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 2rem;
`;

const Nav = styled.nav`
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
`;

const NavList = styled.ul`
  display: flex;
  gap: 2rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const NavLink = styled(Link)<{ active?: boolean }>`
  color: ${({ active }) => active ? 'var(--primary)' : 'var(--text-gray)'};
  font-weight: ${({ active }) => active ? '500' : '400'};
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: var(--text-dark);
  }
`;

const MobileMenuButton = styled.button`
  display: block;
  @media (min-width: 768px) {
    display: none;
  }
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--text-gray);
  transition: color 0.3s;

  &:hover {
    color: var(--text-dark);
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 80%;
  max-width: 300px;
  background: white;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 100;
  transform: translateX(${({ isOpen }) => isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  padding-top: 5rem;
`;

const MobileNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MobileNavLink = styled(Link)<{ active?: boolean }>`
  display: block;
  padding: 0.75rem 1.5rem;
  color: ${({ active }) => active ? 'var(--primary)' : 'var(--text-gray)'};
  font-weight: ${({ active }) => active ? '500' : '400'};
  text-decoration: none;
  border-bottom: 1px solid var(--border);
  transition: all 0.3s;

  &:hover {
    color: var(--text-dark);
    background: var(--gray-50);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--text-gray);
  transition: color 0.3s;

  &:hover {
    color: var(--text-dark);
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => location.pathname === path;

  const handleProductsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const productsSection = document.getElementById('products');
      if (productsSection) {
        const headerOffset = 80; // Height of the header
        const elementPosition = productsSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate('/#products');
    }
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoContainer>
          <Link to="/">
            <Logo src="/images/logo.png" alt="Ota Tofu" />
          </Link>
        </LogoContainer>

        <Nav>
          <NavList>
            <li><NavLink to="/" active={isActive('/')}>Home</NavLink></li>
            <li><NavLink to="/#products" onClick={handleProductsClick}>Products</NavLink></li>
            <li><NavLink to="/about" active={isActive('/about')}>About</NavLink></li>
            <li><NavLink to="/contact" active={isActive('/contact')}>Contact</NavLink></li>
            <li><NavLink to="https://tofumerch.com" target="_blank" rel="noopener noreferrer">Merch Store</NavLink></li>
            <li><NavLink to="https://shop.otapdx.com" target="_blank" rel="noopener noreferrer">Buy Tofu Crumbles</NavLink></li>
          </NavList>
        </Nav>

        <MobileMenuButton onClick={() => setIsMobileMenuOpen(true)}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </MobileMenuButton>
      </HeaderContent>

      <MobileMenu isOpen={isMobileMenuOpen}>
        <CloseButton onClick={() => setIsMobileMenuOpen(false)}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </CloseButton>
        <MobileNavList>
          <li><MobileNavLink to="/" active={isActive('/')}>Home</MobileNavLink></li>
          <li><MobileNavLink to="/#products" onClick={handleProductsClick}>Products</MobileNavLink></li>
          <li><MobileNavLink to="/about" active={isActive('/about')}>About</MobileNavLink></li>
          <li><MobileNavLink to="/contact" active={isActive('/contact')}>Contact</MobileNavLink></li>
          <li><MobileNavLink to="https://tofumerch.com" target="_blank" rel="noopener noreferrer">Merch Store</MobileNavLink></li>
        </MobileNavList>
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header; 
 
 